import PropTypes from "prop-types";
import {
  convertToTitleCase,
  getDateWithFormat,
  prettyColumnName,
} from "../../../utils/commons";
import "./commonTableView.scss";

const getNestedValue = (obj, path) => {
  if (!path || !obj) return undefined;
  return path.split(".").reduce((current, key) => current?.[key], obj);
};

const renderStatusBadge = (value) => {
  const statusClass =
    value && typeof value === "string"
      ? value.toLowerCase().replace(/[\s_]/g, "-")
      : "default";

  return (
    <div className="performances-widget-status-type">
      <span className={`performances-lifecycle-status ${statusClass}`}>
        {value?.replace(/_/g, " ") || "N/A"}
      </span>
    </div>
  );
};

const renderArrayJoin = (value, columnConfigEntry, colKey) => {
  if (!Array.isArray(value)) {
    return value != null ? String(value) : "N/A";
  }

  const joinField = columnConfigEntry.joinField || null;
  if (!joinField) {
    return value.join(", ") || "N/A";
  }

  const isOutcomeField = joinField === "outcome" || colKey === "outcomeName";

  const mapFunction = isOutcomeField
    ? (item) => convertToTitleCase(item[joinField] ?? "N/A")
    : (item) => item[joinField] ?? "N/A";

  return value.map(mapFunction).join(", ") || "N/A";
};

const renderDefaultValue = (value) => {
  if (Array.isArray(value) && value.length === 0) {
    return "N/A";
  }
  return value != null ? String(value) : "N/A";
};

const renderCellValue = (colKey, value, columnConfigEntry, programTimezone) => {
  // If a fully custom render is defined, use that
  if (typeof columnConfigEntry?.render === "function") {
    return columnConfigEntry.render(value);
  }

  const { type } = columnConfigEntry || {};

  switch (type) {
    case "statusBadge":
      return renderStatusBadge(value);
    case "date":
      return value ? getDateWithFormat(value, programTimezone) : "N/A";
    case "arrayJoin":
      return renderArrayJoin(value, columnConfigEntry, colKey);
    default:
      return renderDefaultValue(value);
  }
};
/**
 * Transforms the content list by flattening any fields defined in the columnConfig with `flatten: true`.
 * For each such field, it extracts the first object in the array and creates new keys in the format
 * `${originalKey}_${fieldKey}`.
 * Additionally, it handles nested value paths defined in the columnConfig with `valuePath`.
 */
const transformContentList = (contentList, columnConfig) => {
  return contentList.map((row) => {
    let newRow = { ...row };
    Object.keys(columnConfig).forEach((key) => {
      const config = columnConfig[key];

      if (config?.flatten && Array.isArray(row[key]) && row[key].length > 0) {
        if (config.joinArrayValues) {
          config.fields.forEach((field) => {
            if (field.key === "outcome") {
              newRow[`${key}_${field.key}`] =
                row[key]
                  .map((item) => convertToTitleCase(item[field.key] || "N/A"))
                  .join(", ") || "N/A";
            } else {
              newRow[`${key}_${field.key}`] =
                row[key].map((item) => item[field.key] || "N/A").join(", ") || "N/A";
            }
          });
        } else {
          const flattenedItem = row[key][0];
          config.fields.forEach((field) => {
            newRow[`${key}_${field.key}`] = flattenedItem[field.key];
          });
        }
        // Optionally remove the original field if it's no longer needed:
        // delete newRow[key];
      }

      if (config?.valuePath && !Object.prototype.hasOwnProperty.call(newRow, key)) {
        newRow[key] = getNestedValue(row, config.valuePath);
      }
    });
    return newRow;
  });
};

/**
 * Helper to get the column configuration for a given key.
 * It checks both the top-level keys and any flattened fields.
 */
const getColumnConfigEntry = (colKey, columnConfig) => {
  if (columnConfig[colKey]) {
    return columnConfig[colKey];
  } else {
    // Check if this colKey belongs to a flattened field
    for (const key in columnConfig) {
      if (columnConfig[key]?.flatten) {
        const fieldConfig = columnConfig[key].fields.find(
          (field) => `${key}_${field.key}` === colKey
        );
        if (fieldConfig) {
          return { label: fieldConfig.label };
        }
      }
    }
  }
  return null;
};

const CommonTableView = ({
  contentList = [],
  columnConfig = {},
  programTimezone,
  excludeColumns = [],
  cellClickHandlers = {},
  noResultsFound = null,
  noDataLabel = "No data available",
}) => {
  if (!contentList.length && !noResultsFound) {
    return <p>No data available</p>;
  }

  const transformedContentList = transformContentList(contentList, columnConfig);

  // Get all unique column keys from the transformed data and filter out excluded columns
  const columns = Array.from(
    new Set(transformedContentList.flatMap((row) => Object.keys(row)))
  ).filter((colKey) => !excludeColumns.includes(colKey));

  return (
    <div className="performances-container">
      <div className="performances-list-container">
        <table className="performances-list-table">
          <thead>
            <tr>
              {columns.map((colKey) => {
                const configEntry = getColumnConfigEntry(colKey, columnConfig);
                const label =
                  (configEntry && configEntry.label) || prettyColumnName(colKey);
                const customClass = configEntry?.headerClass;
                return (
                  <th
                    key={colKey}
                    className={
                      customClass ? `performances-column-${customClass}` : ""
                    }
                  >
                    {label}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {transformedContentList.length ? (
              transformedContentList.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {columns.map((colKey) => {
                    const rawValue = row[colKey];
                    const configEntry = getColumnConfigEntry(colKey, columnConfig);
                    const cellContent = renderCellValue(
                      colKey,
                      rawValue,
                      configEntry,
                      programTimezone
                    );
                    return (
                      <td key={colKey}>
                        {cellClickHandlers[colKey] ? (
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              cellClickHandlers[colKey](row);
                            }}
                          >
                            {cellContent}
                          </a>
                        ) : (
                          cellContent
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            ) : (
              <tr>
                {noResultsFound != null ? null : (
                  <td colSpan={columns.length}>{noDataLabel}</td>
                )}
              </tr>
            )}
          </tbody>
        </table>
        {noResultsFound && transformedContentList?.length === 0 && noResultsFound}
      </div>
    </div>
  );
};
CommonTableView.propTypes = {
  contentList: PropTypes.array.isRequired,
  columnConfig: PropTypes.object,
  programTimezone: PropTypes.string,
  excludeColumns: PropTypes.array,
  cellClickHandlers: PropTypes.object,
};

export default CommonTableView;
